import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../../App";
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  BookOpen,
  GitBranch,
  BarChart3,
  Settings,
  Users,
  Bell,
  Search,
  Menu,
  X,
  LogOut,
  Moon,
  Sun,
  ClipboardList,
  Shield,
  ShieldCheck,
  ScrollText,
  UserCheck,
  Eye,
  CheckCircle2,
  XCircle,
  RotateCcw,
  PenTool,
  AlertTriangle,
  Send,
  Info,
  FileSearch,
  HelpCircle,
  Building2,
} from "lucide-react";
import { SecurityNav } from "./SecurityNav";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/contracts", label: "Contracts", icon: FileText },
  { path: "/compliance", label: "Compliance", icon: ShieldCheck },
  { path: "/contract-management", label: "Management", icon: ClipboardList },
  { path: "/workflows", label: "Workflows", icon: GitBranch },
  { path: "/templates", label: "Templates", icon: FolderOpen },
  { path: "/clauses", label: "Clause Library", icon: BookOpen },
  { path: "/supplier-review", label: "Supplier Template Review", icon: FileSearch },
  { path: "/reports", label: "Reports", icon: BarChart3 },
];

const notifIcons = {
  review_requested: { el: <Eye size={14} className="text-blue-600" />, bg: "bg-blue-100 dark:bg-blue-900/40" },
  approval_requested: { el: <CheckCircle2 size={14} className="text-amber-600" />, bg: "bg-amber-100 dark:bg-amber-900/40" },
  revision_requested: { el: <RotateCcw size={14} className="text-orange-600" />, bg: "bg-orange-100 dark:bg-orange-900/40" },
  contract_approved: { el: <CheckCircle2 size={14} className="text-emerald-600" />, bg: "bg-emerald-100 dark:bg-emerald-900/40" },
  contract_rejected: { el: <XCircle size={14} className="text-red-600" />, bg: "bg-red-100 dark:bg-red-900/40" },
  expiration: { el: <AlertTriangle size={14} className="text-amber-600" />, bg: "bg-amber-100 dark:bg-amber-900/40" },
  compliance: { el: <Shield size={14} className="text-purple-600" />, bg: "bg-purple-100 dark:bg-purple-900/40" },
  signature_requested: { el: <PenTool size={14} className="text-indigo-600" />, bg: "bg-indigo-100 dark:bg-indigo-900/40" },
  contract_signed: { el: <PenTool size={14} className="text-emerald-600" />, bg: "bg-emerald-100 dark:bg-emerald-900/40" },
  draft_shared: { el: <Send size={14} className="text-cyan-600" />, bg: "bg-cyan-100 dark:bg-cyan-900/40" },
  system: { el: <Info size={14} className="text-slate-600" />, bg: "bg-slate-100 dark:bg-slate-800" },
};

function getNotifIcon(type) {
  return notifIcons[type] || notifIcons.system;
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const adminItems = [
  { path: "/users", label: "User Management", icon: Users },
  { path: "/approval-policies", label: "Approval Policies", icon: Shield },
  { path: "/audit-log", label: "Audit Log", icon: ScrollText },
  { path: "/access-review", label: "Access Review", icon: UserCheck, adminOnly: true },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [darkMode, setDarkMode] = useState(false);
  const [orgName, setOrgName] = useState("");

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        apiClient.get("/notifications?limit=20"),
        apiClient.get("/notifications/unread-count"),
      ]);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.count);
    } catch (e) {
      /* Notifications load silently */
    }
  }, []);

  const fetchOrgName = useCallback(async () => {
    try {
      const res = await apiClient.get("/organization");
      setOrgName(res.data?.org_name || "");
    } catch (e) { /* Org name is optional UI */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchOrgName();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchOrgName]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  const markAllRead = async () => {
    try {
      await apiClient.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      /* Silent — notification badge will update on next poll */
    }
  };

  const markOneRead = async (notifId) => {
    try {
      await apiClient.put(`/notifications/${notifId}/read`);
      setNotifications((prev) => prev.map((n) => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      /* Silent — notification badge will update on next poll */
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "superuser" || user?.role === "admin" || user?.role === "legal";
  const isSuperuser = user?.role === "superuser";
  const userPermissions = user?.page_permissions || [];
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";

  const filteredNavItems = useMemo(() =>
    navItems.filter((item) => {
      const page = item.path.replace("/", "");
      return userPermissions.length === 0 || userPermissions.includes(page);
    }),
    [userPermissions]
  );

  const filteredAdminItems = useMemo(() =>
    adminItems.filter((item) => {
      if (item.adminOnly) return user?.role === "admin" || user?.role === "superuser";
      const page = item.path.replace("/", "");
      return userPermissions.length === 0 || userPermissions.includes(page);
    }),
    [userPermissions, user?.role]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = React.useRef(null);

  const handleSearch = (val) => {
    setSearchQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.length < 2) { setSearchResults([]); setSearchOpen(false); return; }
    setSearchOpen(true);
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await apiClient.get(`/contract-management/full-text-search?q=${encodeURIComponent(val)}`);
        setSearchResults(res.data.slice(0, 10));
      } catch (e) { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
  };

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className={`sidebar flex flex-col [body.cookie-banner-open_&]:pb-28 ${sidebarOpen ? "open" : ""}`}>
        {/* Logo */}
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800">
          <Link to="/dashboard" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_approve-renew/artifacts/gbpf9q13_ProcureAide%20Logo%20with%20Ai.png" 
              alt="ProcureAide Logo"
              className="h-10 w-auto"
            />
          </Link>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 min-h-0 py-4">
          <nav className="px-3 space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path === "/contracts" && location.pathname.startsWith("/contracts") && !location.pathname.startsWith("/contract-management"));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive ? "active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon size={18} strokeWidth={1.5} />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}

            {isAdmin && (
              <>
                <div className="pt-4 pb-2 px-4">
                  <span className="text-xs font-mono uppercase tracking-wider text-slate-400">
                    Admin
                  </span>
                </div>
                {filteredAdminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-item ${isActive ? "active" : ""}`}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Icon size={18} strokeWidth={1.5} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </>
            )}

            <SecurityNav onNavigate={() => setSidebarOpen(false)} />

            {/* Help - visible to all roles */}
            <div className="pt-4 pb-2 px-4">
              <span className="text-xs font-mono uppercase tracking-wider text-slate-400">Support</span>
            </div>

            {/* Platform Admin - superuser only */}
            {isSuperuser && (
              <Link
                to="/platform"
                className={`nav-item ${location.pathname === "/platform" ? "active" : ""}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-platform-admin"
              >
                <Building2 size={18} strokeWidth={1.5} />
                <span className="text-sm font-medium">Tenant Admin</span>
              </Link>
            )}

            <Link
              to="/help"
              className={`nav-item ${location.pathname === "/help" ? "active" : ""}`}
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-help-&-guide"
            >
              <HelpCircle size={18} strokeWidth={1.5} />
              <span className="text-sm font-medium">Help & Guide</span>
            </Link>
          </nav>
        </ScrollArea>

        {/* User section - DataRefinery style */}
        <div className="border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
          {/* User info */}
          <div className="px-5 pt-4 pb-3">
            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
              {user?.name}
            </p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300 capitalize" data-testid="sidebar-role-badge">
                {user?.role?.replace(/_/g, " ")}
              </span>
              <span className="text-[11px] text-slate-400 truncate">{user?.email}</span>
            </div>
            {orgName && (
              <p className="text-[11px] text-slate-400 mt-1.5 truncate" data-testid="sidebar-org-name">{orgName}</p>
            )}
          </div>
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-5 py-3 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-red-600 dark:hover:text-red-400 border-t border-slate-100 dark:border-slate-800 transition-colors"
            data-testid="sidebar-logout-btn"
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content flex flex-col">
        {/* Topbar */}
        <header className="topbar flex items-center justify-between px-6">
          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 -ml-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Search (Desktop) - hidden on dashboard */}
          {!isDashboard && (
          <div className="hidden md:flex items-center gap-2 flex-1 max-w-md relative">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search contracts, addenda..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => { if (searchQuery.length >= 2) setSearchOpen(true); }}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-cyan-500"
                data-testid="search-input"
              />
            </div>
            {searchOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-sm shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="search-results-dropdown">
                {searchLoading ? (
                  <div className="p-4 text-center text-sm text-slate-400">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((r) => (
                    <button
                      key={`${r.id}-${r.type}`}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b dark:border-slate-700 last:border-0 transition-colors"
                      onMouseDown={() => {
                        navigate(`/contracts/${r.id}`);
                        setSearchOpen(false);
                        setSearchQuery("");
                      }}
                      data-testid={`search-result-${r.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-900 dark:text-white truncate">{r.title}</span>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-sm ${r.type === "addendum" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}>
                          {r.type}
                        </span>
                      </div>
                      {r.snippet && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{r.snippet}</p>
                      )}
                      {r.supplier && <p className="text-xs text-slate-400 mt-0.5">{r.supplier}</p>}
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-sm text-slate-400">No results found for &ldquo;{searchQuery}&rdquo;</div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Right section */}
          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              className="rounded-sm"
              data-testid="theme-toggle"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </Button>

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative rounded-sm" data-testid="notifications-btn">
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <span className="font-semibold text-sm">Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
                      data-testid="mark-all-read-btn"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length > 0 ? (
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.map((notif) => {
                      const icon = getNotifIcon(notif.type);
                      const timeAgo = formatTimeAgo(notif.created_at);
                      return (
                        <div
                          key={notif.id}
                          className={`flex items-start gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 ${
                            !notif.is_read ? "bg-cyan-50/50 dark:bg-cyan-950/20" : ""
                          }`}
                          onClick={() => {
                            if (!notif.is_read) markOneRead(notif.id);
                            if (notif.link) navigate(notif.link);
                          }}
                          data-testid={`notification-item-${notif.id}`}
                        >
                          <div className={`mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${icon.bg}`}>
                            {icon.el}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm truncate ${!notif.is_read ? "font-semibold" : "font-medium text-slate-600 dark:text-slate-400"}`}>
                                {notif.title}
                              </p>
                              {!notif.is_read && (
                                <span className="w-2 h-2 rounded-full bg-cyan-500 shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">{notif.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{timeAgo}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-slate-400">
                    <Bell size={24} className="mx-auto mb-2 opacity-30" />
                    No notifications yet
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 bg-slate-50 dark:bg-slate-950">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
